import { useLocalStorage } from "@/persist";
import { useCallback, useEffect, useRef } from "react";

type ChangelogState = {
  version: string;
  count: number;
  dontShow: boolean;
  lang: string;
};

export function useChangelogPopup(lang: string, changelogVersion: string, maxCount = 3) {
  const targetVersion = changelogVersion.trim();

  const [state, setState] = useLocalStorage<ChangelogState>(
    "changelog",
    (v: any): v is ChangelogState =>
      typeof v?.version === "string" &&
      typeof v?.count === "number" &&
      typeof v?.dontShow === "boolean" &&
      typeof v?.lang === "string",
    { version: "", count: 0, dontShow: false, lang: "" },
    "persist-by-default",
  );

  // useLocalStorage's setter is stable, but its functional updates capture the initial state.
  // Keep the latest changelog state here so callbacks remain stable and consecutive updates compose.
  const stateRef = useRef(state);
  stateRef.current = state;
  const updateState = useCallback(
    (update: (current: ChangelogState) => ChangelogState) => {
      const nextState = update(stateRef.current);
      stateRef.current = nextState;
      setState(nextState);
    },
    [setState],
  );

  useEffect(() => {
    if (!targetVersion || (stateRef.current.lang === lang && stateRef.current.version === targetVersion)) {
      return;
    }

    updateState(() => ({ version: targetVersion, count: 0, dontShow: false, lang }));
  }, [targetVersion, lang, updateState]);

  const incrementCount = useCallback(
    () => updateState((current) => ({ ...current, count: current.count + 1 })),
    [updateState],
  );
  const dontShowAgain = useCallback(() => updateState((current) => ({ ...current, dontShow: true })), [updateState]);

  const loading = !!targetVersion && (state.version !== targetVersion || state.lang !== lang);
  const shouldShow = !!targetVersion && !loading && !state.dontShow && state.count < maxCount;

  return {
    loading,
    shouldShow,
    version: targetVersion || null,
    count: state.count,
    incrementCount,
    dontShowAgain,
  };
}
